// src/index.tsx
import { GoogleOAuthProvider } from "@react-oauth/google";
import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { persistor, store } from "./redux/store";
import logger from "utils/logger";
// Theme with switchable variants
import { ThemeProvider } from "./theme";
// Internationalization (i18n)
import { I18nProvider } from "./i18n";

import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./msalConfig";

// optional: hide noisy AbortError and ResizeObserver logs in dev
const origError = console.error;
console.error = (...args) => {
  if (args[0] instanceof DOMException && args[0].name === "AbortError") return;
  if (
    typeof args[0] === "string" &&
    args[0].includes("The user aborted a request")
  )
    return;
  // Suppress ResizeObserver loop errors (harmless browser warnings)
  if (
    typeof args[0] === "string" &&
    args[0].includes("ResizeObserver loop")
  )
    return;
  origError(...args);
};

// ResizeObserver error suppression is handled in public/index.html (inline script)
// so it fires before the webpack dev overlay registers its handler.

const rootElement = document.getElementById("root");

async function bootstrap() {
  // 1) MSAL v3 must be initialized
  await msalInstance.initialize();

  // 2) Process any redirect response globally BEFORE React renders
  //    (This will populate cache/accounts so we can acquire silently later.)
  try {
    await msalInstance.handleRedirectPromise();
  } catch (e) {
    logger.error("MSAL handleRedirectPromise error:", e);
  }

  if (!rootElement) return;
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider defaultTheme="athena_gradient">
            <I18nProvider>
              <MsalProvider instance={msalInstance}>
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}
                >
                  <HelmetProvider>
                    <ErrorBoundary>
                      <App />
                    </ErrorBoundary>
                  </HelmetProvider>
                </GoogleOAuthProvider>
              </MsalProvider>
            </I18nProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  );
}

bootstrap();
